import React, { useContext, useEffect } from 'react'
import { useRouter, usePathname } from 'next/navigation'

import { AutoComplete, Spin, Tooltip } from 'antd'
import { DefaultOptionType } from 'antd/es/select'

import { config } from '@fortawesome/fontawesome-svg-core'
import '@fortawesome/fontawesome-svg-core/styles.css'
config.autoAddCss = false
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import '@/src/lib'
import { trackGeolocationEvents } from '@/src/lib'
import { AmplitudeContext } from '@/src/contexts/Amplitude/AmplitudeContext'
import { QueryStringParams } from '@/src/interfaces'

interface LocationSearchBarProps {
  value: string
  onChange: (value: string) => void
  onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void
  onBlur: () => void
  onSelect: (value: string) => void
  onClear: () => void
  options: DefaultOptionType[]
  loadingGeoLocation: boolean
  hasCurrentLocation: boolean
  geoLocation: {
    latitude: number
    longitude: number
  } | null
  geoLocationError: string | null
  addressData: {
    fullAddress: string
  } | null
  searchParams: QueryStringParams
  setSearchParams: (params: QueryStringParams) => void
  fetchLocation: () => void
}

export const LocationSearchBar: React.FC<LocationSearchBarProps> = ({
  value,
  onChange,
  onKeyDown,
  onBlur,
  onSelect,
  onClear,
  options,
  loadingGeoLocation,
  hasCurrentLocation,
  geoLocation,
  geoLocationError,
  addressData,
  searchParams,
  setSearchParams,
  fetchLocation,
}) => {
  const router = useRouter()
  const pathname = usePathname()
  const { trackAmplitudeEvent } = useContext(AmplitudeContext)

  // Track location events in a separate effect
  useEffect(() => {
    if (geoLocationError) {
      trackGeolocationEvents.error(trackAmplitudeEvent)({
        'Error Message': geoLocationError,
      })
    } else if (geoLocation) {
      trackGeolocationEvents.success(trackAmplitudeEvent)({
        Latitude: geoLocation.latitude,
        Longitude: geoLocation.longitude,
      })
      trackGeolocationEvents.searchBarIconClicked(trackAmplitudeEvent)({
        'Current Location Exists': !!geoLocation,
      })
    }
  }, [geoLocation, geoLocationError, addressData, trackAmplitudeEvent])

  const handleGeolocationClick = async () => {
    try {
      // Check if geolocation is supported
      if (!navigator.geolocation) {
        return
      }

      // Request location permission first
      await fetchLocation()

      // Set search params to sort by distance regardless of current route
      setSearchParams({
        ...searchParams,
        sortOrder: 'asc',
        sortBy: 'distance',
      })

      // If we're on the home page, redirect to search after getting location
      if (pathname !== '/search') {
        router.push('/search')
      }
    } catch (error) {
      // Error is already handled by useCurrentLocation hook
      console.error('Error getting geolocation:', error)
    }
  }

  // Render location options
  const renderLocationOptions = (option: DefaultOptionType) => {
    if (!option.highlightedName) {
      return option
    }

    return {
      ...option,
      label: (
        <div className='flex items-center justify-between'>
          <span
            dangerouslySetInnerHTML={{
              __html: option.highlightedName as string,
            }}
          />
        </div>
      ),
    }
  }

  const formattedOptions = options
    .map((option) => renderLocationOptions(option))
    .filter(Boolean)

  return (
    <div className='flex flex-col w-full'>
      <AutoComplete
        allowClear
        className='custom-select w-full lg:min-w-[250px]'
        style={{
          height: '5rem',
          borderRight: 'none',
        }}
        data-testid='location-input'
        aria-label='Enter location'
        value={value}
        onChange={onChange}
        onKeyDown={onKeyDown}
        onBlur={onBlur}
        onSelect={onSelect}
        options={formattedOptions}
        placeholder='City, State, or Zip Code'
        size='large'
        onClear={onClear}
        prefix={
          <Tooltip
            title={
              hasCurrentLocation
                ? 'This is your current location'
                : 'Get your current location'
            }
          >
            <div
              className={`mr-2 ${
                hasCurrentLocation
                  ? 'cursor-not-allowed text-gray-400'
                  : 'cursor-pointer text-primary hover:text-secondary'
              }`}
              onClick={handleGeolocationClick}
              role='button'
              aria-label='Use my current location'
            >
              {loadingGeoLocation ? (
                <Spin size='small' />
              ) : (
                <FontAwesomeIcon icon='location-dot' />
              )}
            </div>
          </Tooltip>
        }
      />
      {geoLocationError && (
        <div className='text-red-500 text-sm mt-2' data-testid='location-error'>
          {geoLocationError}
        </div>
      )}
    </div>
  )
}
